
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FormStep } from "@/components/listings/filters/FilterSchema";
import { useListingWizardContext } from "@/context/ListingWizardContext";
import { CheckCircle2, Circle, CircleEllipsis } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";

const FormStepTabs = () => {
  const { currentStep, completedSteps, setCurrentStep } = useListingWizardContext();
  const isMobile = useIsMobile();

  const renderStepIndicator = (step: number) => {
    const isCompleted = completedSteps[step];
    const isCurrent = currentStep === step;
    
    return (
      <div className="absolute -right-1 -top-1">
        {isCompleted ? (
          <CheckCircle2 className="h-5 w-5 text-green-600 bg-white rounded-full" />
        ) : isCurrent ? (
          <CircleEllipsis className="h-5 w-5 text-propertylk-blue bg-white rounded-full" />
        ) : (
          <Circle className="h-5 w-5 text-gray-300 bg-white rounded-full" />
        )}
      </div>
    );
  };

  const renderStepContent = (step: number) => {
    const isCompleted = completedSteps[step];
    const isCurrent = currentStep === step;
    
    return (
      <div className="text-center">
        <div className="text-xs font-medium mb-1">Step {step + 1}</div>
        <div className="font-semibold text-sm whitespace-nowrap">
          {step === 0 && "Basic Info"}
          {step === 1 && "Details"}
          {step === 2 && "Location"}
          {step === 3 && "Media"}
          {step === 4 && "Contact"}
          {step === 5 && "Title & Desc"}
          {step === 6 && "Review"}
        </div>
      </div>
    );
  };

  const handleStepChange = (step: number) => {
    if (completedSteps[step] || step === currentStep) {
      setCurrentStep(step);
    }
  };

  // Mobile step selector with sheet
  if (isMobile) {
    return (
      <div className="mb-6">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full flex items-center justify-between bg-white">
              <span className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-propertylk-blue text-white text-xs">
                  {currentStep + 1}
                </span>
                <span className="font-medium">
                  {currentStep === 0 && "Basic Info"}
                  {currentStep === 1 && "Details"}
                  {currentStep === 2 && "Location"}
                  {currentStep === 3 && "Media"}
                  {currentStep === 4 && "Contact"}
                  {currentStep === 5 && "Title & Desc"}
                  {currentStep === 6 && "Review"}
                </span>
              </span>
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[80vh]">
            <div className="py-4">
              <h3 className="font-semibold text-lg mb-4">Navigate Steps</h3>
              <div className="space-y-3">
                {[0, 1, 2, 3, 4, 5, 6].map((step) => {
                  const isCompleted = completedSteps[step];
                  const isCurrent = currentStep === step;
                  return (
                    <button 
                      key={step}
                      className={`w-full text-left p-3 rounded-md border flex items-start gap-3
                        ${isCompleted ? "border-green-500 bg-green-50" : ""}
                        ${isCurrent && !isCompleted ? "border-propertylk-blue bg-propertylk-lightblue/30" : ""}
                        ${!isCurrent && !isCompleted ? "border-gray-200 bg-gray-50 opacity-60" : ""}
                      `}
                      disabled={!isCompleted && step !== currentStep}
                      onClick={() => handleStepChange(step)}
                    >
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white border text-xs">
                        {step + 1}
                      </div>
                      <div>
                        <div className="font-medium">
                          {step === 0 && "Basic Info"}
                          {step === 1 && "Details"}
                          {step === 2 && "Location"}
                          {step === 3 && "Media"}
                          {step === 4 && "Contact"}
                          {step === 5 && "Title & Desc"}
                          {step === 6 && "Review"}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {isCompleted && "Completed"}
                          {isCurrent && !isCompleted && "Current step"}
                          {!isCurrent && !isCompleted && "Not yet completed"}
                        </div>
                      </div>
                      <div className="ml-auto">
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : isCurrent ? (
                          <CircleEllipsis className="h-5 w-5 text-propertylk-blue" />
                        ) : (
                          <Circle className="h-5 w-5 text-gray-300" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Desktop step tabs
  return (
    <TabsList className="grid grid-cols-7 mb-8 p-0 h-auto bg-transparent gap-1">
      {[0, 1, 2, 3, 4, 5, 6].map((step) => {
        const isCompleted = completedSteps[step];
        const isCurrent = currentStep === step;
        
        return (
          <TabsTrigger 
            key={`step-${step}`} 
            value={`step-${step}`} 
            className={`flex flex-col items-center py-3 px-2 relative rounded-md border transition-colors
              ${isCompleted ? "border-green-500 text-green-700 bg-green-50" : ""}
              ${isCurrent && !isCompleted ? "border-propertylk-blue text-propertylk-blue bg-propertylk-lightblue/20" : ""}
              ${!isCurrent && !isCompleted ? "border-gray-200 text-gray-400 bg-gray-50" : ""}
              ${isCurrent ? "shadow-sm" : ""}
              hover:bg-gray-50 hover:text-gray-700
            `}
            disabled={!isCompleted && step !== currentStep && step !== 0}
          >
            {renderStepIndicator(step)}
            {renderStepContent(step)}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
};

export default FormStepTabs;
